import React, { useState } from "react";
import { ArrowRight, Download, Loader2, Pencil, Printer } from "lucide-react";
import { RelabelCaseRecord } from "src/types";
import { ROUTING_TYPE_CONFIG, RoutingTypeKey } from "src/constants/relabelCases";

interface SubmittedCaseCardProps {
  relabelCaseRecord: RelabelCaseRecord;
  onDownloadLabel: (caseId: string) => Promise<void>;
  onPrintLabel: () => void;
  isPrinterConnected: boolean;
  printedInfo?: { printerName: string };
  onEditProduct?: (caseId: string, productId: string) => void;
}

const SubmittedCaseCard: React.FC<SubmittedCaseCardProps> = ({
  relabelCaseRecord,
  onDownloadLabel,
  onPrintLabel,
  isPrinterConnected,
  printedInfo,
  onEditProduct
}) => {
  const [isDownloading, setIsDownloading] = useState(false);

  const config = ROUTING_TYPE_CONFIG[relabelCaseRecord.routingType as RoutingTypeKey];
  const activeProducts = relabelCaseRecord.products.filter((p) => p.quantity > 0);

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      await onDownloadLabel(relabelCaseRecord.id);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className={`${config.bgColor} ${config.borderColor} border-2 border-l-[6px] rounded-xl overflow-hidden`}>
      <div className="flex items-center justify-between px-5 py-3">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="font-mono text-sm font-bold text-secondary-900">{relabelCaseRecord.id}</span>
          <span className={`${config.badgeBg} ${config.badgeText} text-xs font-semibold px-2.5 py-1 rounded-full`}>
            {config.label}
          </span>
          {relabelCaseRecord.isAnimalSku && (
            <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-amber-50 text-amber-800 border border-amber-200">
              Animal
            </span>
          )}
          {relabelCaseRecord.isAnimalNonCertified && (
            <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-orange-50 text-orange-800 border border-orange-200">
              Animal (Non-Certified)
            </span>
          )}
          <span className="text-xs text-secondary-500">{relabelCaseRecord.totalUnits} units</span>
          <span className={`flex items-center gap-1 text-xs font-medium ${config.textColor}`}>
            <ArrowRight className="w-3 h-3" />
            {config.laneLabel}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleDownload}
            disabled={isDownloading}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-white border border-secondary-300 rounded-lg hover:bg-secondary-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isDownloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
            {isDownloading ? "Downloading..." : "Download"}
          </button>
          <div className="flex flex-col items-end gap-0.5">
            <button
              type="button"
              onClick={onPrintLabel}
              disabled={!isPrinterConnected}
              title={!isPrinterConnected ? "Install Zebra Browser Print to enable direct printing" : undefined}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white rounded-lg transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${
                printedInfo ? "bg-success-600 hover:bg-success-700" : "bg-black hover:bg-gray-800"
              }`}
            >
              <Printer className="w-3.5 h-3.5" />
              {printedInfo ? "ZPL Reprint" : "ZPL Print"}
            </button>
            {printedInfo && (
              <span
                className="text-[10px] text-success-700 font-medium truncate max-w-[180px]"
                title={printedInfo.printerName}
              >
                Printed · {printedInfo.printerName}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="px-5 pb-4 flex flex-wrap gap-2">
        {activeProducts.map((product) => {
          const showEdit = !!onEditProduct && relabelCaseRecord.status === "PENDING_SCAN_IN";
          const dateLabel =
            product.productDate && product.dateType ? `${product.dateType}: ${product.productDate}` : undefined;
          return (
            <div
              key={product.id}
              className="inline-flex items-center gap-2 bg-white/70 border border-secondary-200 rounded-lg px-3 py-2"
            >
              <span className="text-xs text-secondary-500">PO: {relabelCaseRecord.purchaseOrderId}</span>
              <span className="text-secondary-300">|</span>
              <span className="font-mono text-sm font-bold text-secondary-800">{product.euSkuName}</span>
              <span className="text-sm font-semibold text-secondary-700">×{product.quantity}</span>
              {dateLabel && (
                <>
                  <span className="text-secondary-300">|</span>
                  <span className="text-[11px] text-secondary-600">{dateLabel}</span>
                </>
              )}
              {product.lotNumber && (
                <>
                  <span className="text-secondary-300">|</span>
                  <span className="text-[11px] text-secondary-600 font-mono">Lot {product.lotNumber}</span>
                </>
              )}
              {showEdit && (
                <button
                  type="button"
                  onClick={() => onEditProduct!(relabelCaseRecord.id, product.id)}
                  title="Edit quantity / date / lot"
                  className="ml-1 inline-flex items-center justify-center w-6 h-6 rounded text-secondary-500 hover:text-secondary-900 hover:bg-secondary-100 transition-colors"
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export { SubmittedCaseCard };
